import React, { useState } from 'react';
import { SequenceEvent } from '@/services/api';
import { getActionColor, formatZone, formatAction, getZoneLabel } from './helpers';

const GoalSequenceTimeline: React.FC<{ sequence: SequenceEvent[]; defaultExpanded?: boolean }> = ({ sequence, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const COLLAPSED_COUNT = 8;
  const needsCollapse = sequence.length > COLLAPSED_COUNT;
  const displayEvents = expanded || !needsCollapse ? sequence : sequence.slice(sequence.length - COLLAPSED_COUNT);
  const hiddenCount = needsCollapse && !expanded ? sequence.length - COLLAPSED_COUNT : 0;

  return (
    <div className="ml-4 border-l-2 border-gray-200 dark:border-gray-700 pl-4 space-y-2">
      {hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
          Show full build-up ({hiddenCount} more events)
        </button>
      )}
      {displayEvents.map((event) => {
        const isGoal = event.actionDetail === 'goal';
        const isFoul = event.action === 'foul' || event.action === 'infraction';
        return (
          <div
            key={`${event.minute}-${event.second}-${event.action}`}
            className={`flex items-start gap-3 py-1.5 ${(() => {
              if (isGoal) return 'bg-green-50/50 dark:bg-green-900/10 rounded-lg px-2 -ml-2';
              if (isFoul) return 'bg-red-50/50 dark:bg-red-900/10 rounded-lg px-2 -ml-2';
              return '';
            })()}`}
          >
            {/* Time */}
            <div className="flex-shrink-0 w-12 text-right">
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                {event.minute}'{event.second > 0 ? `${event.second}"` : ''}
              </span>
            </div>

            {/* Dot on timeline */}
            <div className={`flex-shrink-0 w-2.5 h-2.5 rounded-full mt-1 ${(() => {
              if (isGoal) return 'bg-green-500';
              if (isFoul) return 'bg-red-500';
              return 'bg-gray-300 dark:bg-gray-600';
            })()}`} />

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded ${(() => {
                  if (isGoal) return 'bg-green-600 text-white';
                  if (isFoul) return 'bg-red-600 text-white';
                  return getActionColor(event.action);
                })()}`}>
                  {(() => {
                    if (isGoal) return 'GOAL';
                    if (isFoul) return 'FOUL';
                    return formatAction(event.actionDetail || event.action);
                  })()}
                </span>
                {event.player && (
                  <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                    {event.player.name}
                  </span>
                )}
                {event.accurate === false && (
                  <span className="text-xs text-red-500 dark:text-red-400">missed</span>
                )}
              </div>
              {event.location && (
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {formatZone(getZoneLabel(event.location.x, event.location.y))}
                  {event.endLocation && (
                    <> &rarr; {formatZone(getZoneLabel(event.endLocation.x, event.endLocation.y))}</>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {expanded && needsCollapse && (
        <button
          onClick={() => setExpanded(false)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          Collapse
        </button>
      )}
    </div>
  );
};

export default GoalSequenceTimeline;
